export type Channel = "email" | "sms";

export type TriggerDirection = "before" | "after" | "on";

export type Touch = {
  id: string;
  ch: Channel;
  title: string;
  /** Days from the anchor. Ignored when `dir` is "on". */
  days: number;
  dir: TriggerDirection;
  /** Key-date code the offset is measured from. */
  anchor: string;
  /** Send hour, local to the campaign's quiet-hours zone. */
  hr: number;
  /** Group within the sequence, for playbooks that split into phases. */
  grp?: string;
  on?: boolean;
  /** Published in the catalog. An unpublished touch cannot be switched on. */
  pub: boolean;
  ver: number;
  updated?: string;
  /** Edited for this campaign, away from the catalog original. */
  customized?: boolean;
  /** This touch's own approval row. A touch is approved or returned individually. */
  launchAdId?: number | null;
  reviewStatus?: string | null;
  reviewComment?: string;
  // Email
  subject?: string;
  pre?: string;
  body?: string;
  cta?: string;
  plain?: string;
  // SMS
  text?: string;
};

export type SequenceGroup = {
  id: string;
  name: string;
  note: string;
};

export type Sequence = {
  id: string;
  name: string;
  stage: string;
  /** What the sequence is trying to get the student to do. */
  target: string;
  /** Plain-language description of the enrolment window it covers. */
  win: string;
  starter: string;
  published: boolean;
  groups: SequenceGroup[];
  /*
      Which channels the PLAYBOOK carries. Not derivable from `touches`: the server leaves a
      switched-off channel's touches out entirely, so reading it from there loses the SMS
      toggle the moment SMS is switched off, and there is then no way to switch it back on.
  */
  emailCount: number;
  smsCount: number;
  touches: Touch[];
};

export type SequenceSettings = {
  on: boolean;
  emailCh: boolean;
  smsCh: boolean;
  /** "campaign" reuses the campaign audience; "separate" picks its own. */
  audMode: "campaign" | "separate";
  audPick: string;
};

export type KeyDateRef = {
  id: number;
  type: string;
  label: string;
  date: string;
};

/*
    Display names for the handful of codes the prototype was built against. The real
    KeyDateType table carries its own labels and almost none of its codes appear here —
    `term_start`, `registration_end`, `target_launch_go_live` — so this is the last resort
    behind the loaded ones, not the source of truth.
*/
export const KEY_DATE_NAMES: Record<string, string> = {
  app: "Application Deadline",
  reg: "Registration Deadline",
  term: "Term Start",
  priority: "Priority Deadline",
  deposit: "Deposit Deadline",
  start: "Campaign Start",
  end: "Campaign End",
  /*
      The codes `seed_sequence_vocabulary` actually creates, and the ones the loaded playbooks
      anchor to. Without them the catalog -- which has no campaign, so no loaded KeyDateType
      labels to win over this map -- fell through to the raw code and read "On campaign_start".
  */
  campaign_start: "Campaign Start",
  campaign_end: "Campaign End",
  registration_end: "Registration Deadline",
  registration_start: "Registration Opens",
  term_start: "Term Start",
  term_end: "Term End",
  orientation_date: "Orientation",
};

export const RATE_EMAIL = 0.0001;
export const RATE_SMS = 0.0083;

/** "3 days before Application Deadline", "1 day after Campaign Start", or "On Term Start". */
export function triggerWords(days: number | "", dir: TriggerDirection, anchorName: string): string {
  if (dir === "on") return `On ${anchorName}`;
  // Every email/SMS pair in the playbooks puts the text one day after its email, so "1 days
  // after" was on 117 of the 236 catalog rows.
  const unit = days === 1 ? "day" : "days";
  return `${days === "" ? "?" : days} ${unit} ${dir} ${anchorName}`;
}

export function anchorName(code: string): string {
  return KEY_DATE_NAMES[code] ?? code;
}

/*
    What to call the date a touch anchors to.

    The loaded key date wins: `KeyDateType` rows carry their own label, and a campaign may
    have renamed one. KEY_DATE_NAMES stays as the fallback for an anchor with no key date on
    this campaign — which is the missing-key-date warning, the one case where a readable name
    is what makes the warning mean anything.
*/
export function anchorLabel(
  code: string,
  keyDates: KeyDateRef[],
  typeLabels: Record<string, string> = {},
): string {
  const match = keyDates.find((k) => k.type === code);
  return match?.label?.trim() || typeLabels[code] || anchorName(code);
}

/*
    A calendar day, read as that day where the reader is.

    `new Date("2027-03-02")` is parsed as UTC midnight, which is 1 March anywhere west of
    UTC — so every send date computed from a key date came out a day early in the Americas.
    It went unnoticed while the key dates were three invented constants; it ships real send
    dates now, and a message scheduled a day early is a message sent on the wrong day.
*/
export function parseDay(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    const loose = new Date(value);
    return isNaN(loose.getTime()) ? null : loose;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** The date a touch lands, given the key dates in force. */
export function sendDate(touch: Touch, keyDates: KeyDateRef[]): Date | null {
  const anchor = keyDates.find((k) => k.type === touch.anchor);
  if (!anchor?.date) return null;
  const base = parseDay(anchor.date);
  if (!base) return null;
  if (touch.dir === "on") return base;
  const shift = touch.dir === "before" ? -touch.days : touch.days;
  base.setDate(base.getDate() + shift);
  return base;
}

/*
    An SMS that repeats its paired email's title reads as one unit with it. The pair is
    tinted rather than joined by a line, and the band alternates so neighbouring pairs
    stay apart.
*/
export type PairInfo = { pairStart: boolean; pairEnd: boolean; pairBand: 0 | 1 };

export function markPairs(touches: Touch[]): Map<string, PairInfo> {
  const marks = new Map<string, PairInfo>();
  touches.forEach((t) => marks.set(t.id, { pairStart: false, pairEnd: false, pairBand: 0 }));
  let band = 0;
  for (let i = 0; i < touches.length - 1; i++) {
    const a = touches[i];
    const b = touches[i + 1];
    if (a.title && a.title === b.title && a.ch !== b.ch) {
      const value = (band++ % 2) as 0 | 1;
      marks.set(a.id, { pairStart: true, pairEnd: false, pairBand: value });
      marks.set(b.id, { pairStart: false, pairEnd: true, pairBand: value });
      i++;
    }
  }
  return marks;
}
